import { useCallback, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import clsx from 'clsx';
import * as R from 'ramda';

import { GET_WORK_ORDER_TEMPLATE } from '@atom/graph/workTemplate';
import { Button } from '@atom/mui';
import { WorkOrdersConnectionInput } from '@atom/types/work';

import TaskFiltersSection from './TaskFiltersSection';
import TeamFiltersSection from './TeamFiltersSection';
import WorkFiltersSection from './WorkFiltersSection';
import WorkOrdersFiltersContext, {
  FILTER_DEFAULTS,
  FilterForm,
} from './WorkOrdersFiltersContext';
import { buildWorkOrdersFilterInput } from './workOrdersFilterUtilities';

import classes from './filters.module.css';

type WorkOrdersFilterPanelProps = {
  schemaId: string;
  loadingWorkOrders: boolean;
  onFiltersApply: (filters: WorkOrdersConnectionInput) => void;
};

const WorkOrdersFilterPanel = ({
  schemaId,
  loadingWorkOrders,
  onFiltersApply,
}: WorkOrdersFilterPanelProps) => {
  const [filterForm, setFilterForm] = useState<FilterForm>(FILTER_DEFAULTS);

  const { workTemplates } = filterForm;

  const [
    getWorkOrderTemplate,
    {
      data: { workOrderTemplate } = { workOrderTemplate: null },
      loading: loadingWorkTemplateData,
    },
  ] = useLazyQuery(GET_WORK_ORDER_TEMPLATE);

  const selectedTemplateId =
    workTemplates.length === 1 ? workTemplates[0]?.id : null;

  useEffect(() => {
    // the template field filters are keyed by field ids that only exist on the
    // selected template, so they must not outlive the selection - their inputs
    // unmount with it, leaving an unreachable filter behind
    setFilterForm(current =>
      R.isEmpty(current.workTemplateFields) &&
      R.isEmpty(current.workTemplateTaskFields)
        ? current
        : {
            ...current,
            workTemplateFields: FILTER_DEFAULTS.workTemplateFields,
            workTemplateTaskFields: FILTER_DEFAULTS.workTemplateTaskFields,
          },
    );

    if (selectedTemplateId) {
      getWorkOrderTemplate({
        variables: {
          id: selectedTemplateId,
        },
      });
    }
  }, [selectedTemplateId]);

  const updateFilterForm = useCallback(
    (field: keyof FilterForm, value: any) =>
      setFilterForm(current => ({
        ...current,
        [field]: typeof value === 'function' ? value(current[field]) : value,
      })),
    [],
  );

  const onApplyFilters = () =>
    onFiltersApply(buildWorkOrdersFilterInput(filterForm));

  const onResetFilters = () => {
    setFilterForm(FILTER_DEFAULTS);
    onFiltersApply({});
  };

  const areFiltersDisabled = loadingWorkOrders || loadingWorkTemplateData;

  return (
    <WorkOrdersFiltersContext.Provider
      value={{
        schemaId,
        areFiltersDisabled,
        filterForm,
        workOrderTemplate: selectedTemplateId ? workOrderTemplate : null,
        updateFilterForm,
      }}
    >
      <div
        className={clsx(
          classes.header,
          classes.filterSection,
          classes.stickyTop,
        )}
      >
        <div className={classes.headerTitle}>Filters</div>
        <div className={classes.headerButtonContainer}>
          <Button
            onClick={onResetFilters}
            disabled={areFiltersDisabled}
            data-cy="workOrdersFilterResetButton"
          >
            Reset
          </Button>
          <Button
            color="primary"
            onClick={onApplyFilters}
            disabled={areFiltersDisabled}
            data-cy="workOrdersFilterApplyButton"
          >
            Apply
          </Button>
        </div>
      </div>
      <WorkFiltersSection />
      <TaskFiltersSection />
      <TeamFiltersSection />
    </WorkOrdersFiltersContext.Provider>
  );
};

export default WorkOrdersFilterPanel;
